import type { ComponentProps } from 'react';
import { Button, Tooltip } from '@customerio/pluma-components/react';
import { usePermissions } from '@customerio/hydra-permissions/react';
import { useAuth } from '@/services/auth';
import { PlanFeature } from '@/services/auth/types';
import { GatedFeaturesPopover } from '@/features/gated-features';
import { useProductTaxonomy } from '@/services/product-taxonomy';
import { useCollectionFormModal } from './collection-form-modal';

// Per-env max_collection_size isn't surfaced in Hydra yet; 50 is the default. Follow-up to plumb it.
export const MAX_COLLECTION_COUNT = 50;

type AddCollectionButtonProps = {
  workspaceId: number;
  existingNames: string[];
  variant?: ComponentProps<typeof Button>['variant'];
};

export function AddCollectionButton({ workspaceId, existingNames, variant = 'primary' }: AddCollectionButtonProps) {
  const { account } = useAuth();
  const { hasPermission } = usePermissions();
  const taxonomy = useProductTaxonomy();
  const collectionModal = useCollectionFormModal();

  const isEssentialsTrial = account.suspend_trial_at != null;

  if (!account.planFeatures[PlanFeature.Collections].enabled) {
    return (
      <GatedFeaturesPopover
        featureName="Collections"
        title={`Unlock Collections with ${isEssentialsTrial ? 'a Premium Trial' : 'Premium'}`}
        text={`With a Premium ${isEssentialsTrial ? 'Trial' : 'Plan'} you can store collections of data in your workspace that you can use in ${taxonomy.term('campaigns.plural')} with Liquid.`}
        variant={variant}
      >
        Add collection
      </GatedFeaturesPopover>
    );
  }

  const canCreate = hasPermission('journeys/collections/create') === true;
  const reachedLimit = existingNames.length >= MAX_COLLECTION_COUNT;

  const addButton = (
    <Button
      variant={variant}
      isDisabled={!canCreate || reachedLimit}
      onClick={() => collectionModal.open({ workspaceId, existingNames })}
    >
      Add collection
    </Button>
  );

  if (reachedLimit) {
    return (
      <Tooltip
        content={`Each workspace cannot have more than ${MAX_COLLECTION_COUNT} collections. Remove a collection to continue.`}
      >
        {addButton}
      </Tooltip>
    );
  }

  return addButton;
}
